import { createRouter, createWebHistory } from 'vue-router';
import IndexPage from '@/pages/IndexPage.vue'; // Updated path and name
// Login is a redirect, no direct component import needed for it here if it stays redirect.
import AuthPage from '@/pages/AuthPage.vue'; // Updated path and name
import CasdoorCallback from '@/pages/CasdoorCallback.vue';
import UserProfile from '@/pages/UserProfile.vue'; // Updated path and name
import NotificationsPage from '@/pages/NotificationsPage.vue';
import CreateSimulationPage from '@/pages/simulation/CreateSimulationPage.vue'; // Updated path and name
import SimulationResultsPage from '@/pages/simulation/SimulationResultsPage.vue'; // Updated path and name
import SimulationHistoryPage from '@/pages/simulation/SimulationHistoryPage.vue'; // Updated path and name
// Collections Module Pages
import CollectionsPage from '@/pages/CollectionsPage.vue'; // Path updated
import CollectionDetailPage from '@/pages/CollectionDetailPage.vue';
import CollectionEditCreatePage from '@/pages/CollectionEditCreatePage.vue';
import CompareSimulationsPage from '@/pages/simulation/CompareSimulationsPage.vue'; // Updated path and name

import HelpCenterPage from '@/pages/HelpCenterPage.vue';
import FeedbackPage from '@/pages/FeedbackPage.vue';
import AboutEyuanPage from '@/pages/AboutEyuanPage.vue';
import TermsOfServicePage from '@/pages/TermsOfServicePage.vue';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage.vue';
import RefundPolicyPage from '@/pages/RefundPolicyPage.vue';

/**
 * @file router/index.js
 * @description Vue Router configuration for the application.
 * Includes route definitions, meta fields for authentication control,
 * and a global navigation guard (`beforeEach`) to enforce authentication rules.
 */

/**
 * Checks if the current user is authenticated.
 * This simple mock implementation checks for the presence of an 'authToken' in localStorage.
 * A more robust implementation might involve decoding a JWT to check its expiry and validity.
 * @returns {boolean} True if the user is considered authenticated, false otherwise.
 */
function isAuthenticated() {
  const token = localStorage.getItem('authToken');
  // Basic check: token presence.
  // For a JWT, this could be enhanced:
  // if (!token) return false;
  // try {
  //   const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload (unsafe for real JWTs, use a library)
  //   return payload.exp * 1000 > Date.now(); // Check expiry if 'exp' is a UNIX timestamp
  // } catch (e) {
  //   console.error("Error parsing or validating token:", e);
  //   localStorage.removeItem('authToken'); // Clear invalid token
  //   return false;
  // }
  return !!token; // Returns true if token exists and is not an empty string, false otherwise.
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  /**
   * Route definitions for the application.
   * Each route object can contain:
   * @property {string} path - The URL path for the route.
   * @property {string} [name] - A unique name for the route (optional, but good for programmatic navigation).
   * @property {Vue.Component} component - The Vue component to render for this route.
   * @property {string} [redirect] - A path to redirect to if this route is matched.
   * @property {object} [meta] - Meta fields for storing route-specific information.
   * @property {boolean} [meta.requiresAuth] - If true, this route requires authentication.
   * @property {boolean} [meta.guestOnly] - If true, this route is only accessible to unauthenticated users.
   */
  routes: [
    {
      path: '/',
      name: 'IndexPage', // Updated name
      component: IndexPage, // Updated component
      // meta: { requiresAuth: true } // Example: Uncomment if Home page also needs authentication
    },
    {
      path: '/login', // Legacy route, redirects to new Casdoor auth flow
      name: 'Login', // Name can remain as Login if it's a conceptual route name
      redirect: '/auth'
    },
    {
      path: '/auth', // Page for Casdoor login/signup buttons
      name: 'AuthPage', // Updated name
      component: AuthPage, // Updated component
      meta: { guestOnly: true } // This page should only be accessible to unauthenticated users
    },
    {
      path: '/profile', // User profile page
      name: 'UserProfile', // Updated name
      component: UserProfile, // Updated component
      meta: { requiresAuth: true } // Requires authentication
    },
    {
      path: '/notifications',
      name: 'Notifications', // Assuming NotificationsPage is the actual component name now
      component: NotificationsPage,
      meta: { requiresAuth: true } // This route requires authentication
    },
    {
      path: '/simulation/create',
      name: 'CreateSimulationPage', // Updated name
      component: CreateSimulationPage, // Updated component
      meta: { requiresAuth: true } // Requires authentication
    },
    {
      path: '/simulation/results/:id', // Added :id parameter
      name: 'SimulationResultsPage', // Updated name
      component: SimulationResultsPage, // Updated component
      props: true, // Pass route params as props
      meta: { requiresAuth: true } // Requires authentication
    },
    {
      path: '/simulation/history',
      name: 'SimulationHistoryPage', // Updated name
      component: SimulationHistoryPage, // Updated component
      meta: { requiresAuth: true } // Requires authentication
    },
    {
      path: '/collections',
      name: 'CollectionsPage', // Updated name (was CollectionsList)
      component: CollectionsPage, // Component import name matches new file name
      meta: { requiresAuth: true }
    },
    {
      path: '/collections/create',
      name: 'CollectionCreate',
      component: CollectionEditCreatePage,
      meta: { requiresAuth: true }
    },
    {
      path: '/collections/:id',
      name: 'CollectionDetail',
      component: CollectionDetailPage,
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/collections/edit/:id',
      name: 'CollectionEdit',
      component: CollectionEditCreatePage,
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/forgot-password', // Legacy route, redirects to Casdoor auth flow
      name: 'ForgotPassword',
      redirect: '/auth' // Casdoor handles password recovery via its UI
    },
    {
      path: '/help',
      name: 'HelpCenter',
      component: HelpCenterPage
    },
    {
      path: '/about',
      name: 'AboutEyuan',
      component: AboutEyuanPage
    },
    {
      path: '/feedback',
      name: 'Feedback',
      component: FeedbackPage
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfService',
      component: TermsOfServicePage
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: PrivacyPolicyPage
    },
    {
      path: '/refund-policy',
      name: 'RefundPolicy',
      component: RefundPolicyPage
    },
    {
      path: '/callback', // Casdoor OAuth callback URI
      name: 'CasdoorCallback',
      component: CasdoorCallback
      // This route does not need requiresAuth/guestOnly as it handles its own logic
      // and redirects based on the outcome of the Casdoor signin process.
    },
    {
      path: '/simulation/compare',
      name: 'CompareSimulationsPage', // Updated name
      component: CompareSimulationsPage, // Updated component
      // props: route => ({ queryIds: route.query.ids }), // Passing query as props is one way
      meta: { requiresAuth: true } // Requires authentication
    },
    {
      path: '/trading/entry',
      name: 'EntryMonitoringPage',
      component: () => import('@/pages/trading/EntryMonitoringPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/trading/exit',
      name: 'ExitMonitoringPage',
      component: () => import('@/pages/trading/ExitMonitoringPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/strategy/performance',
      name: 'StrategyPerformancePage',
      component: () => import('@/pages/strategy/StrategyPerformancePage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/strategy/performance/:strategyId',
      name: 'StrategyDetailPage',
      component: () => import('@/pages/strategy/StrategyDetailPage.vue'),
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/strategy/performance/:strategyId/recommendations',
      name: 'StrategyRecommendationsPage',
      component: () => import('@/pages/strategy/StrategyRecommendationsPage.vue'),
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/strategy/performance/:strategyId/assets/:assetId/indicators',
      name: 'StrategyAssetIndicatorsPage',
      component: () => import('@/pages/strategy/StrategyAssetIndicatorsPage.vue'),
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/subscription/plans',
      name: 'SubscriptionPlansPage',
      component: () => import('@/pages/SubscriptionPlansPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/payment/history',
      name: 'PaymentHistoryPage',
      component: () => import('@/pages/PaymentHistoryPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/payment/result',
      name: 'PaymentResultPage',
      component: () => import('@/pages/PaymentResultPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/invitation/manage',
      name: 'InvitationCodeManagementPage',
      component: () => import('@/pages/InvitationCodeManagementPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/referral/dashboard',
      name: 'ReferralDashboardPage',
      component: () => import('@/pages/ReferralDashboardPage.vue'),
      meta: { requiresAuth: true }
    }
  ]
});

/**
 * Global navigation guard.
 * This function is executed before each navigation.
 * It checks the route's meta fields (`requiresAuth`, `guestOnly`) and the user's
 * authentication status (`isAuthenticated()`) to control access to routes.
 *
 * @param {import('vue-router').RouteLocationNormalized} to - The target Route Object being navigated to.
 * @param {import('vue-router').RouteLocationNormalized} from - The current Route Object being navigated away from.
 * @param {import('vue-router').NavigationGuardNext} next - Function to resolve the hook. Must be called to continue navigation.
 */
router.beforeEach((to, from, next) => {
  const isPublicSubscriptionPage = to.matched.some(record => record.name === 'SubscriptionPlansPage');
  const requiresAuth = !isPublicSubscriptionPage && to.matched.some(record => record.meta.requiresAuth);
  const guestOnly = to.matched.some(record => record.meta.guestOnly);
  const authenticated = isAuthenticated();

  if (requiresAuth && !authenticated) {
    // If the route requires authentication and the user is not authenticated,
    // redirect to the /auth page.
    console.log(`[RouterGuard] Path "${to.path}" requires auth. User not authenticated. Redirecting to /auth.`);
    next({ name: 'AuthPage' }); // Use named redirect for clarity, updated to AuthPage
  } else if (guestOnly && authenticated) {
    // If the route is marked as guest-only (e.g., login/signup page)
    // and the user is already authenticated, redirect to the home page.
    console.log(`[RouterGuard] Path "${to.path}" is guest-only. User authenticated. Redirecting to /.`);
    next({ name: 'IndexPage' }); // Redirect to home or a default authenticated page, updated to IndexPage
  } else {
    // Otherwise, allow navigation.
    next();
  }
});

export default router;
